import type {
  CalendarReconcileSubmitInput,
  CalendarReconcileSubmitResult,
} from '@blocksuite/affine/data-view';
import {
  isServerDatabaseRootDocId,
  isServerDatabaseRowShardDocId,
} from '@streamlain/database-identity';
import { Service } from '@toeverything/infra';
import { Subject } from 'rxjs';

import { isBackendKnownUnavailable } from '../../../utils/backend-availability';
import {
  OfflineMutationDomainError,
  OfflineMutationOutboxService,
  OfflineMutationTransientError,
} from '../../offline-mutation-outbox/services/offline-mutation-outbox';
import type {
  CalendarAttachSourceMutationPayload,
  CalendarCreateMailboxLinkMutationPayload,
  CalendarCreateSourceMutationPayload,
  CalendarDeleteMailboxLinkMutationPayload,
  CalendarReconcileEventMutationPayload,
  CalendarRespondInvitationMutationPayload,
  CalendarSourceRefMutationPayload,
  CalendarUpdateMailboxLinkMutationPayload,
  CalendarUpdateSourcePreferenceMutationPayload,
  OfflineMutationRecord,
} from '../../offline-mutation-outbox/types';
import type { WorkspaceService } from '../../workspace';
import {
  CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
  ViewBackedAppMutationOutboxService,
} from '../view-backed-app';
import {
  CalendarApiClientService,
  CalendarHttpError,
  type CalendarMailboxLinkCreateInput,
  CalendarServerUnavailableError,
} from './calendar-api-client';
import type {
  CalendarInvitationResponseStatus,
  CalendarMailboxLinkPatch,
  CalendarSourceAttachInput,
  CalendarSourcePreferencePatch,
  CalendarSourceRef,
} from './types';

export type CalendarMutationOperation =
  | 'calendar.attachSource'
  | 'calendar.createMailboxLink'
  | 'calendar.createSource'
  | 'calendar.deleteMailboxLink'
  | 'calendar.reconcileEvent'
  | 'calendar.respondInvitation'
  | 'calendar.updateMailboxLink'
  | 'calendar.updateSourcePreference';

export type CalendarMutationRecord =
  OfflineMutationRecord<CalendarMutationOperation>;

export class CalendarMutationOutboxService extends Service {
  readonly completed$ = new Subject<CalendarMutationRecord>();
  readonly failed$ = new Subject<CalendarMutationRecord>();
  readonly pendingMutations$ = this.outbox.pendingMutations$;
  readonly failedMutations$ = this.outbox.failedMutations$;

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly calendarApi: CalendarApiClientService,
    private readonly outbox: OfflineMutationOutboxService,
    private readonly viewBackedOutbox: ViewBackedAppMutationOutboxService
  ) {
    super();
    this.registerExecutors();
    const failedSubscription = this.outbox.failed$.subscribe(mutation => {
      if (isCalendarMutation(mutation)) {
        this.failed$.next(mutation);
      }
    });
    const completedSubscription = this.outbox.completed$.subscribe(mutation => {
      if (isCalendarMutation(mutation)) {
        this.completed$.next(mutation);
      }
    });
    this.disposables.push(() => failedSubscription.unsubscribe());
    this.disposables.push(() => completedSubscription.unsubscribe());
  }

  enqueueCreateSource(input: CalendarSourceAttachInput): Promise<unknown> {
    return this.viewBackedOutbox.enqueueCreateSource(
      CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
      input
    );
  }

  enqueueAttachSource(input: CalendarSourceAttachInput): Promise<unknown> {
    return this.viewBackedOutbox.enqueueAttachSource(
      CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
      input
    );
  }

  enqueueUpdateSourcePreference(
    sourceId: string,
    patch: CalendarSourcePreferencePatch
  ): Promise<unknown> {
    return this.viewBackedOutbox.enqueueUpdateSourcePreference(
      CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
      sourceId,
      patch
    );
  }

  enqueueCreateMailboxLink(
    source: CalendarSourceRef,
    input: CalendarMailboxLinkCreateInput
  ): Promise<unknown> {
    return this.outbox.enqueue({
      operation: 'calendar.createMailboxLink',
      payload: { source, input },
      idempotencyKey: [
        'calendar.createMailboxLink',
        this.workspaceService.workspace.id,
        source.docId,
        source.databaseId,
        source.viewId,
        input.mailAccountId,
        input.inboundEnabled,
        input.outboundEnabled,
      ].join(':'),
    });
  }

  enqueueUpdateMailboxLink(
    source: CalendarSourceRef,
    linkId: string,
    patch: CalendarMailboxLinkPatch
  ): Promise<unknown> {
    return this.outbox.enqueue({
      operation: 'calendar.updateMailboxLink',
      payload: { source, linkId, patch },
      idempotencyKey: [
        'calendar.updateMailboxLink',
        this.workspaceService.workspace.id,
        source.docId,
        source.databaseId,
        source.viewId,
        linkId,
        patch.inboundEnabled ?? '-',
        patch.outboundEnabled ?? '-',
        patch.status ?? '-',
      ].join(':'),
    });
  }

  enqueueDeleteMailboxLink(
    source: CalendarSourceRef,
    linkId: string
  ): Promise<unknown> {
    return this.outbox.enqueue({
      operation: 'calendar.deleteMailboxLink',
      payload: { source, linkId },
      idempotencyKey: [
        'calendar.deleteMailboxLink',
        this.workspaceService.workspace.id,
        source.docId,
        source.databaseId,
        source.viewId,
        linkId,
      ].join(':'),
    });
  }

  enqueueRespondInvitation(
    source: CalendarSourceRef,
    rowId: string,
    status: CalendarInvitationResponseStatus,
    icsSequence?: number | null
  ): Promise<unknown> {
    const requestId = globalThis.crypto.randomUUID();
    return this.outbox.enqueue({
      operation: 'calendar.respondInvitation',
      payload: { icsSequence, requestId, rowId, source, status },
      idempotencyKey: [
        'calendar.respondInvitation',
        this.workspaceService.workspace.id,
        source.docId,
        source.databaseId,
        source.viewId,
        rowId,
        status,
        requestId,
      ].join(':'),
    });
  }

  async enqueueReconcileEvent(
    input: CalendarReconcileSubmitInput
  ): Promise<CalendarMutationRecord> {
    const workspaceId = this.workspaceService.workspace.id;
    if (input.context.workspaceId !== workspaceId) {
      throw new OfflineMutationDomainError(
        'Calendar reconcile workspace does not match the active workspace'
      );
    }
    const syncDocIds = [
      ...new Set(
        [input.context.docId, ...input.context.syncDocIds].filter(
          docId => docId !== workspaceId
        )
      ),
    ];
    const requestId = globalThis.crypto.randomUUID();
    return this.outbox.enqueue({
      operation: 'calendar.reconcileEvent',
      payload: {
        columns: input.columns,
        databaseId: input.context.databaseId,
        docId: input.context.docId,
        previousEvent: input.previousEvent,
        requestId,
        rowId: input.rowId,
        skipMail: input.skipMail,
        syncDocIds,
        viewId: input.viewId,
      },
      idempotencyKey: [
        'calendar.reconcileEvent',
        workspaceId,
        input.context.docId,
        input.context.databaseId,
        input.rowId,
        requestId,
      ].join(':'),
    });
  }

  async submitReconcileEvent(
    input: CalendarReconcileSubmitInput
  ): Promise<CalendarReconcileSubmitResult> {
    const mutation = await this.enqueueReconcileEvent(input);

    if (
      (typeof navigator !== 'undefined' && navigator.onLine === false) ||
      isBackendKnownUnavailable()
    ) {
      return 'pending';
    }

    await this.outbox.flush();
    const pending = await this.outbox.refreshPendingMutations();
    if (pending.some(record => record.id === mutation.id)) {
      return 'pending';
    }
    const failed = await this.outbox.refreshFailedMutations();
    const failedMutation = failed.find(record => record.id === mutation.id);
    if (failedMutation) {
      throw new OfflineMutationDomainError(
        failedMutation.lastError ?? 'Calendar reconcile failed'
      );
    }
    return 'completed';
  }

  retryFailed(id: string): Promise<void> {
    return this.outbox.retryFailed(id);
  }

  removeFailed(id: string): Promise<boolean> {
    return this.outbox.removeFailed(id);
  }

  private registerExecutors(): void {
    this.disposables.push(
      this.outbox.registerExecutor(
        'calendar.createMailboxLink',
        async mutation => {
          const payload =
            mutation.payload as CalendarCreateMailboxLinkMutationPayload;
          const source = toCalendarSourceRef(payload.source);
          try {
            await this.runCalendarRequest(() =>
              this.calendarApi.createMailboxLink(
                mutation.workspaceId,
                source,
                payload.input
              )
            );
          } catch (error) {
            if (
              !(await this.isMailboxLinkAlreadyVisible(
                mutation.workspaceId,
                payload,
                error
              ))
            ) {
              throw error;
            }
          }
        }
      ),
      this.outbox.registerExecutor(
        'calendar.updateMailboxLink',
        async mutation => {
          const payload =
            mutation.payload as CalendarUpdateMailboxLinkMutationPayload;
          const source = toCalendarSourceRef(payload.source);
          await this.runCalendarRequest(() =>
            this.calendarApi.updateMailboxLink(
              mutation.workspaceId,
              source,
              payload.linkId,
              payload.patch
            )
          );
        }
      ),
      this.outbox.registerExecutor(
        'calendar.deleteMailboxLink',
        async mutation => {
          const payload =
            mutation.payload as CalendarDeleteMailboxLinkMutationPayload;
          const source = toCalendarSourceRef(payload.source);
          try {
            await this.runCalendarRequest(() =>
              this.calendarApi.deleteMailboxLink(
                mutation.workspaceId,
                source,
                payload.linkId
              )
            );
          } catch (error) {
            if (!isNotFoundError(error)) {
              throw error;
            }
          }
        }
      ),
      this.outbox.registerExecutor(
        'calendar.reconcileEvent',
        async mutation => {
          const payload =
            mutation.payload as CalendarReconcileEventMutationPayload;
          await this.viewBackedOutbox.prepareSourceForServerMutation(
            CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
            payload
          );
          await this.flushReconcileDocuments(payload.syncDocIds);
          await this.runCalendarRequest(() =>
            this.calendarApi.reconcileEvent(mutation.workspaceId, payload)
          );
        }
      ),
      this.outbox.registerExecutor(
        'calendar.respondInvitation',
        async mutation => {
          const payload =
            mutation.payload as CalendarRespondInvitationMutationPayload;
          const result = await this.runCalendarRequest(() =>
            this.calendarApi.respondToInvitation(
              mutation.workspaceId,
              toCalendarSourceRef(payload.source),
              payload.rowId,
              { requestId: payload.requestId, status: payload.status }
            )
          );
          if (result.delivery.status === 'sent') return;
          if (
            result.delivery.issueReason === 'mail_send_failed' ||
            result.delivery.status === 'failed'
          ) {
            throw new OfflineMutationTransientError(
              'Calendar response delivery failed'
            );
          }
          throw new OfflineMutationDomainError(
            `Calendar response unavailable: ${result.delivery.issueReason ?? 'unknown'}`
          );
        }
      )
    );
  }

  private async flushReconcileDocuments(
    docIds: readonly string[]
  ): Promise<void> {
    const docEngine = this.workspaceService.workspace.engine?.doc;
    if (!docEngine?.flushAndWaitForSynced) {
      throw new OfflineMutationTransientError(
        'Calendar reconcile documents are still syncing'
      );
    }
    const uniqueDocIds = orderCalendarReconcileDocIds(
      [...new Set(docIds.filter(Boolean))],
      this.workspaceService.workspace.id
    );
    const priorities = uniqueDocIds.map(docId => {
      docEngine.addDoc?.(docId, 'database-subdoc');
      return docEngine.addPriority?.(docId, 100);
    });
    const abort = AbortSignal.timeout(10_000);
    try {
      for (const docId of uniqueDocIds) {
        await docEngine.flushAndWaitForSynced(docId, abort);
      }
    } catch {
      throw new OfflineMutationTransientError(
        'Calendar reconcile documents are still syncing'
      );
    } finally {
      priorities.forEach(disposeSyncPriority);
    }
  }

  private async isMailboxLinkAlreadyVisible(
    workspaceId: string,
    payload: CalendarCreateMailboxLinkMutationPayload,
    error: unknown
  ): Promise<boolean> {
    if (!isDuplicateOrAlreadyExistsError(error)) {
      return false;
    }
    const source = toCalendarSourceRef(payload.source);
    const response = await this.runCalendarRequest(() =>
      this.calendarApi.listMailboxLinks(workspaceId, source)
    );
    return response.links.some(
      link =>
        link.mailAccountId === payload.input.mailAccountId &&
        link.inboundEnabled === payload.input.inboundEnabled &&
        link.outboundEnabled === payload.input.outboundEnabled &&
        link.status === 'active'
    );
  }

  private async runCalendarRequest<T>(request: () => Promise<T>): Promise<T> {
    try {
      return await request();
    } catch (error) {
      if (error instanceof CalendarServerUnavailableError) {
        throw new OfflineMutationTransientError(
          'Calendar server is not available'
        );
      }
      if (error instanceof CalendarHttpError && error.status >= 500) {
        throw new OfflineMutationTransientError(
          'Calendar server is temporarily unavailable'
        );
      }
      throw error;
    }
  }
}

export function orderCalendarReconcileDocIds(
  docIds: readonly string[],
  workspaceId: string
): string[] {
  const pageDocs: string[] = [];
  const rowShards: string[] = [];
  const databaseRoots: string[] = [];
  for (const docId of docIds) {
    if (isServerDatabaseRowShardDocId(docId, workspaceId)) {
      rowShards.push(docId);
    } else if (isServerDatabaseRootDocId(docId, workspaceId)) {
      databaseRoots.push(docId);
    } else {
      pageDocs.push(docId);
    }
  }
  return [...pageDocs, ...rowShards, ...databaseRoots];
}

function disposeSyncPriority(disposable: unknown): void {
  if (typeof disposable === 'function') {
    disposable();
    return;
  }
  const value = disposable as
    | { dispose?: unknown; unsubscribe?: unknown }
    | null
    | undefined;
  if (typeof value?.dispose === 'function') {
    value.dispose();
  } else if (typeof value?.unsubscribe === 'function') {
    value.unsubscribe();
  }
}

export function isCalendarMutation(
  mutation: OfflineMutationRecord
): mutation is CalendarMutationRecord {
  return mutation.operation.startsWith('calendar.');
}

export function calendarMutationTargetsSource(
  mutation: CalendarMutationRecord,
  source: Pick<CalendarSourceRef, 'databaseId' | 'docId' | 'viewId'> & {
    id: string;
  }
): boolean {
  if (mutation.operation === 'calendar.updateSourcePreference') {
    const payload =
      mutation.payload as CalendarUpdateSourcePreferenceMutationPayload;
    return payload.sourceId === source.id;
  }

  if (
    mutation.operation === 'calendar.attachSource' ||
    mutation.operation === 'calendar.createSource'
  ) {
    const payload = mutation.payload as
      | CalendarAttachSourceMutationPayload
      | CalendarCreateSourceMutationPayload;
    return calendarSourceIdentityMatches(payload, source);
  }

  if (mutation.operation === 'calendar.reconcileEvent') {
    const payload = mutation.payload as CalendarReconcileEventMutationPayload;
    return calendarSourceIdentityMatches(payload, source);
  }

  const payload = mutation.payload as
    | CalendarCreateMailboxLinkMutationPayload
    | CalendarDeleteMailboxLinkMutationPayload
    | CalendarRespondInvitationMutationPayload
    | CalendarUpdateMailboxLinkMutationPayload;
  return calendarSourceIdentityMatches(payload.source, source);
}

function calendarSourceIdentityMatches(
  mutationSource: Pick<
    CalendarSourceRefMutationPayload,
    'databaseId' | 'docId' | 'viewId'
  >,
  source: Pick<CalendarSourceRef, 'databaseId' | 'docId' | 'viewId'>
): boolean {
  return (
    mutationSource.docId === source.docId &&
    mutationSource.databaseId === source.databaseId &&
    mutationSource.viewId === source.viewId
  );
}

function toCalendarSourceRef(
  source: CalendarSourceRefMutationPayload
): CalendarSourceRef {
  assertCalendarSourceRefValue(
    'appKind',
    source.appKind,
    CALENDAR_VIEW_BACKED_APP_DESCRIPTOR.appKind
  );
  assertCalendarSourceRefValue('blockId', source.blockId);
  assertCalendarSourceRefValue('databaseId', source.databaseId);
  assertCalendarSourceRefValue('docId', source.docId);
  assertCalendarSourceRefValue('viewId', source.viewId);
  assertCalendarSourceRefValue(
    'viewMode',
    source.viewMode,
    CALENDAR_VIEW_BACKED_APP_DESCRIPTOR.viewMode
  );
  return {
    appKind: source.appKind,
    blockId: source.blockId,
    databaseId: source.databaseId,
    docId: source.docId,
    viewId: source.viewId,
    viewMode: source.viewMode,
  };
}

function assertCalendarSourceRefValue(
  key: keyof CalendarSourceRef,
  value: unknown,
  expected?: string
): asserts value is string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new OfflineMutationDomainError(`Calendar source ref ${key} required`);
  }
  if (expected !== undefined && value !== expected) {
    throw new OfflineMutationDomainError(
      `Calendar source ref ${key} must be "${expected}", got "${value}"`
    );
  }
}

function isDuplicateOrAlreadyExistsError(error: unknown): boolean {
  const message = errorDiagnosticText(error).toLowerCase();
  return (
    errorStatus(error) === 409 ||
    /already exists|duplicate|unique|already attached/.test(message)
  );
}

function isNotFoundError(error: unknown): boolean {
  return (
    errorStatus(error) === 404 || /not found/.test(errorDiagnosticText(error))
  );
}

function errorStatus(error: unknown): number {
  if (!error || typeof error !== 'object') {
    return 0;
  }
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : 0;
}

function errorDiagnosticText(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (!error || typeof error !== 'object') {
    return '';
  }
  return [
    (error as { message?: unknown }).message,
    (error as { code?: unknown }).code,
    (error as { type?: unknown }).type,
    (error as { name?: unknown }).name,
  ]
    .filter(value => typeof value === 'string')
    .join(' ');
}
